import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { AssociationType, EventStatusType, ReslinkType, SearchType } from '@marriott/mi-groups-graphql';
import { EventTrackingData } from './../organisms/reslink/ReslinkEventDetails/EventTracking/EventTracking.types';
import { PreApprovedImage } from '../organisms/reslink/ReslinkEventDetails/ImageTray/PreApprovedImages/PreApprovedImages.types';

export type SearchParams = {
  searchType: SearchType;
  searchValue: string;
  startDate: string;
  endDate: string;
};

export type PreApprovedPropertyImages = {
  propertyCount: Record<string, number>;
  propertyImages: Record<string, PreApprovedImage[]>;
};

export type ReslinkAssociateState = SearchParams & {
  loggedIn: boolean;
  isSuperUser: boolean;
  eventStatusFilter: EventStatusType;
  createdByFilter: AssociationType;
  eventTypeFilter: ReslinkType;
  currentPage: number;
  emailLocale: string;
  eventTrackingData: EventTrackingData[];
  eventTrackingPropertyIndex: number;
  hasHydrated: boolean;
  preApprovedPropertyImages: PreApprovedPropertyImages;
};

export type ReslinkAssociateActions = {
  setLoggedIn: (loggedIn: boolean) => void;
  setIsSuperUser: (isSuperUser: boolean) => void;
  setSearchParams: (searchParams: SearchParams) => void;
  setEventStatusFilter: (eventStatus: EventStatusType) => void;
  setCreatedByFilter: (createdBy: AssociationType) => void;
  setEventTypeFilter: (eventType: ReslinkType) => void;
  setCurrentPage: (currentPage: number) => void;
  setEmailLocale: (locale: string) => void;
  setEventTrackingData: (data: EventTrackingData) => void;
  removeEventTrackingData: (index: number) => void;
  resetEventTrackingData: () => void;
  setEventTrackingPropertyIndex: (index: number) => void;
  setHasHydrated: (hydrated: boolean) => void;
  isPropertyExist: (propertyId: string) => boolean;
  setPropertyImages: (propertyId: string, preApprovedImages: PreApprovedImage[]) => void;
  incrementPropertyCount: (propertyId: string) => void;
  removePropertyImages: (propertyId: string) => void;
  resetPropertyImages: () => void;
};

const initialState: ReslinkAssociateState = {
  loggedIn: false,
  isSuperUser: false,
  searchType: SearchType.EVENT_NAME,
  searchValue: '',
  startDate: '',
  endDate: '',
  eventStatusFilter: EventStatusType.ACTIVE,
  createdByFilter: AssociationType.CREATED_BY,
  eventTypeFilter: ReslinkType.GRP,
  currentPage: 1,
  emailLocale: '',
  eventTrackingData: [],
  eventTrackingPropertyIndex: 0,
  hasHydrated: false,
  preApprovedPropertyImages: { propertyCount: {}, propertyImages: {} },
};

export const reslinkAssociateStore: StateCreator<ReslinkAssociateState & ReslinkAssociateActions> = (set, get) => {
  return {
    ...initialState,

    setLoggedIn: (loggedIn: boolean) => {
      set(prevState => {
        return {
          ...prevState,
          loggedIn,
        };
      });
    },

    setIsSuperUser: (isSuperUser: boolean) => {
      set(prevState => {
        return {
          ...prevState,
          isSuperUser,
        };
      });
    },

    setSearchParams: (searchParams: SearchParams) => {
      set(prevState => {
        return {
          ...prevState,
          ...searchParams,
        };
      });
    },

    setEventStatusFilter: (eventStatus: EventStatusType) => {
      set(prevState => {
        return {
          ...prevState,
          eventStatusFilter: eventStatus,
        };
      });
    },

    setCreatedByFilter: (createdBy: AssociationType) => {
      set(prevState => {
        return {
          ...prevState,
          createdByFilter: createdBy,
        };
      });
    },

    setEventTypeFilter: (eventType: ReslinkType) => {
      set(prevState => {
        return {
          ...prevState,
          eventTypeFilter: eventType,
        };
      });
    },

    setCurrentPage: (currentPage: number) => {
      set(prevState => {
        return {
          ...prevState,
          currentPage,
        };
      });
    },

    setEmailLocale: (locale: string) => {
      set(prevState => {
        return {
          ...prevState,
          emailLocale: locale,
        };
      });
    },

    setEventTrackingData: (data: EventTrackingData) => {
      set(prevState => {
        return {
          ...prevState,
          eventTrackingData: [...prevState.eventTrackingData, data],
        };
      });
    },

    removeEventTrackingData: (propertyIndex: number) => {
      set(prevState => {
        return {
          ...prevState,
          eventTrackingData: prevState.eventTrackingData.filter(data => data.propertyIndex !== propertyIndex),
        };
      });
    },

    resetEventTrackingData: () => {
      set(prevState => {
        return {
          ...prevState,
          eventTrackingData: [],
          eventTrackingPropertyIndex: 0,
        };
      });
    },

    setEventTrackingPropertyIndex: (index: number) => {
      set(prevState => {
        return {
          ...prevState,
          eventTrackingPropertyIndex: index,
        };
      });
    },

    isPropertyExist: propertyId => {
      const { preApprovedPropertyImages } = get();
      return propertyId in preApprovedPropertyImages.propertyImages;
    },

    setPropertyImages: (propertyId, images) => {
      set(prevState => {
        const { propertyCount, propertyImages } = prevState.preApprovedPropertyImages;

        return {
          ...prevState,
          preApprovedPropertyImages: {
            propertyCount: {
              ...propertyCount,
              [propertyId]: 1,
            },
            propertyImages: {
              ...propertyImages,
              [propertyId]: images,
            },
          },
        };
      });
    },

    incrementPropertyCount: propertyId => {
      set(prevState => {
        const { propertyCount } = prevState.preApprovedPropertyImages;

        return {
          ...prevState,
          preApprovedPropertyImages: {
            ...prevState.preApprovedPropertyImages,
            propertyCount: {
              ...propertyCount,
              [propertyId]: (propertyCount[propertyId] ?? 0) + 1,
            },
          },
        };
      });
    },

    removePropertyImages: (propertyId: string) => {
      set(prevState => {
        const { propertyCount, propertyImages } = prevState.preApprovedPropertyImages;
        const currentPropertyCount = propertyCount[propertyId] ?? 0;

        if (currentPropertyCount === 1) {
          const { [propertyId]: _omitCount, ...nextCounts } = propertyCount;
          const { [propertyId]: _omitImages, ...nextImages } = propertyImages;

          return {
            ...prevState,
            preApprovedPropertyImages: {
              ...prevState.preApprovedPropertyImages,
              propertyCount: nextCounts,
              propertyImages: nextImages,
            },
          };
        }

        return {
          ...prevState,
          preApprovedPropertyImages: {
            ...prevState.preApprovedPropertyImages,
            propertyCount: {
              ...propertyCount,
              [propertyId]: currentPropertyCount - 1,
            },
          },
        };
      });
    },

    resetPropertyImages: () => {
      set(prevState => {
        return {
          ...prevState,
          preApprovedPropertyImages: {
            propertyCount: {},
            propertyImages: {},
          },
        };
      });
    },

    setHasHydrated: (hydrated: boolean) => set(prevState => ({ ...prevState, hasHydrated: hydrated })),
  };
};

export const useReslinkAssociateStore = createAppStore(reslinkAssociateStore, {
  usePersistentStore: true,
  persistentStoreName: 'ReslinkAssociate',
  useLocalStorage: true,
  partialize: state => {
    const {
      eventTrackingData,
      eventTrackingPropertyIndex,
      preApprovedPropertyImages: _preApprovedPropertyImages,
      ...rest
    } = state;
    return rest as typeof state;
  },
  onRehydrateStorage: () => state => {
    state?.setHasHydrated(true);
  },
});
